import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ContentFieldDto,
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface HthpFluidLossReportValues {
  temperature?: number;
  pressure?: number;
  filtrateVolume?: number;
  waterVolume?: number;
  cakeThickness?: number;
}

@Component({
  selector: 'app-hthp-fluid-loss-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hthp-fluid-loss-test-report.component.html',
  styleUrl: './hthp-fluid-loss-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HthpFluidLossTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected hthpFluidLossReportValues(): HthpFluidLossReportValues | null {
    const temperature = this.numberValue([
      'temperature',
      'testtemperature',
      'testtempf',
      'temperaturef',
      'requestedtemperature'
    ], this.output) ?? this.numberValue([
      'temperature',
      'testtemperature',
      'testtempf',
      'temperaturef',
      'requestedtemperature'
    ], this.test);
    const pressure = this.numberValue([
      'pressure',
      'diffpressure',
      'differentialpressure',
      'diffpressurepsi'
    ], this.output) ?? this.numberValue([
      'pressure',
      'diffpressure',
      'differentialpressure',
      'diffpressurepsi'
    ], this.test);
    const filtrateVolume = this.numberValue([
      'filtrate30min',
      'filtratevolume',
      'hthpfiltrate',
      'hthpfiltrateml',
      'filtrate'
    ], this.output) ?? this.numberValue([
      'filtrate30min',
      'filtratevolume',
      'hthpfiltrate',
      'hthpfiltrateml',
      'filtrate'
    ], this.test);
    const waterVolume = this.numberValue([
      'watervolume',
      'hthpwater',
      'hthpwatercm3',
      'water'
    ], this.output) ?? this.numberValue([
      'watervolume',
      'hthpwater',
      'hthpwatercm3',
      'water'
    ], this.test);
    const cakeThickness = this.numberValue([
      'cakethickness',
      'cakehthp',
      'hthpcake',
      'hthpfiltercake',
      'filtercake',
      'cake32nd',
      'cake32nds',
      'cake32ndsofaninch'
    ], this.output) ?? this.numberValue([
      'cakethickness',
      'cakehthp',
      'hthpcake',
      'hthpfiltercake',
      'filtercake',
      'cake32nd',
      'cake32nds',
      'cake32ndsofaninch'
    ], this.test);

    if ([temperature, pressure, filtrateVolume, waterVolume, cakeThickness]
      .every(value => value === undefined)) {
      return null;
    }

    return { temperature, pressure, filtrateVolume, waterVolume, cakeThickness };
  }

  protected formatValue(value: number | undefined): string {
    return value === undefined ? '—' : String(value);
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const value = this.value(fields, source.content, names);
    return this.toNumber(value);
  }

  private value(fields: ContentFieldDto[], content: string | null, names: string[]): string | null {
    const acceptedNames = new Set(names);
    const parsedField = fields.find(field =>
      acceptedNames.has(this.normalize(field.label)));
    return parsedField?.value?.trim() || this.xmlValue(content, acceptedNames);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}